import { useCallback } from "react";
import { PREMIUM_FEATURES } from "@common/flatRateBilling";
import { useOverlays } from "contexts/OverlayContext";
import { useActiveWorkspace } from "@hooks/useActiveWorkspace";
import { useAllowFeature } from "@hooks/useAllowFeature";
import { useStateAwareNavigate } from "@hooks/useStateAwareNavigate";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { useLegacyPlusEntityLimit } from "@components/billing/LegacyPlusEntityLimit/useLegacyPlusEntityLimit";

interface StartAddEntityOptions {
    returnTo?: string;
}

export function useStartAddEntity() {
    const { newAddClientFlow } = useTypedFlags();
    const navigate = useStateAwareNavigate();
    const workspace = useActiveWorkspace();
    const { openOverlay } = useOverlays();
    const { isAtLimit } = useLegacyPlusEntityLimit();
    const { hasAccess, isFetched } = useAllowFeature(
        PREMIUM_FEATURES.UNLIMITED_ENTITIES,
    );

    return useCallback(
        ({ returnTo }: StartAddEntityOptions = {}) => {
            const navigateToNewEntityPage = () =>
                navigate(
                    "/new-entity",
                    returnTo ? { state: { returnTo } } : undefined,
                );

            if (!newAddClientFlow || !workspace) {
                navigateToNewEntityPage();
                return;
            }

            if (!isFetched) {
                return;
            }

            // Without access the /new-entity route redirects to plans, so it stays the single enforcer.
            if (!hasAccess) {
                navigateToNewEntityPage();
                return;
            }

            if (isAtLimit) {
                openOverlay("legacyPlusEntityLimit", true);
                return;
            }

            openOverlay("addEntity", { workspaceId: workspace.id });
        },
        [
            newAddClientFlow,
            workspace,
            hasAccess,
            isFetched,
            isAtLimit,
            navigate,
            openOverlay,
        ],
    );
}
